import { InboxOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Col,
  Flex,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadProps } from "antd";
import { useState } from "react";
import { Button, Card } from "@rumbledirect/ui";
import {
  importAccidentReports,
  type AccidentReportImportRow,
  type AccidentReportImportResult,
} from "@/lib/api/resources/dataManagement";

const { Dragger } = Upload;

const notFoundColumns = [
  {
    title: "Kenteken",
    dataIndex: "licensePlate",
    key: "licensePlate",
  },
  {
    title: "Schadenummer",
    dataIndex: "damageNumber",
    key: "damageNumber",
  },
];

export const AccidentReportImport = () => {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AccidentReportImportResult | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Geen bestand geselecteerd");
      return importAccidentReports(file);
    },
    onSuccess: (data) => {
      setResult(data);
      message.success("Schadeformulieren verwerkt");
    },
    onError: () => message.error("Verwerken van schadeformulieren mislukt"),
  });

  const props: UploadProps = {
    name: "file",
    multiple: false,
    beforeUpload: (f) => {
      setFile(f);
      setResult(null);
      return false; // prevent auto upload
    },
    onRemove: () => {
      setFile(null);
      setResult(null);
    },
    accept:
      ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    maxCount: 1,
  };

  return (
    <Flex vertical gap={16}>
      <Card title="Schadeformulieren verwerken">
        <Space orientation="vertical" style={{ width: "100%" }} size="large">
          <Alert
            type="info"
            showIcon
            title="Upload een Excel-bestand (.xlsx) met schadeformulieren. De eerste drie kolommen worden gebruikt: kenteken, schadenummer en toegewezen (ja/nee)."
          />
          <Dragger {...props} disabled={mutation.isPending}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <Typography.Paragraph>
              Klik of sleep een Excel-bestand naar dit gebied om te verwerken
            </Typography.Paragraph>
          </Dragger>
          <div>
            <Button
              type="primary"
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={!file}
            >
              Verwerken
            </Button>
          </div>
        </Space>
      </Card>

      {result ? (
        <Card title="Resultaat">
          <Space orientation="vertical" style={{ width: "100%" }} size="large">
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Verwerkte schadeformulieren"
                  value={result.importedCount}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Reeds verwerkte schadeformulieren"
                  value={result.alreadyHasAccidentReport.length}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Niet gevonden"
                  value={result.notFound.length}
                />
              </Col>
            </Row>

            {result.notFound.length > 0 ? (
              <div>
                <Typography.Title level={5}>
                  Niet gevonden schadeformulieren
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  Voor onderstaande schadeformulieren is geen overeenkomend
                  dossier gevonden.
                </Typography.Paragraph>
                <Table<AccidentReportImportRow>
                  rowKey={(row) => `${row.licensePlate}-${row.damageNumber}`}
                  columns={notFoundColumns}
                  dataSource={result.notFound}
                  pagination={false}
                  size="small"
                />
              </div>
            ) : null}
          </Space>
        </Card>
      ) : null}
    </Flex>
  );
};
